import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Accessibility, Sparkles } from "lucide-react";

export default function Guests() {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        const { data } = await api.get("/guests", { params: q ? { q } : {} });
        setItems(data);
        if (data.length && !active) setActive(data[0]);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!active) return;
        api.get(`/guests/${active.id}`).then((r) => setDetail(r.data));
    }, [active]);

    return (
        <>
            <PageHeader
                title="Guests"
                subtitle="Profiles with preferences and accessibility, remembered across stays."
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <Card className="lg:col-span-4">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Directory</CardTitle>
                        <div className="pt-1">
                            <Input
                                placeholder="Search guests"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                data-testid="guests-search"
                            />
                        </div>
                    </CardHeader>
                    <CardContent>
                        {!items ? (
                            <LoadingBlock rows={6} />
                        ) : (
                            <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="guests-list">
                                {items.map((g) => (
                                    <button
                                        key={g.id}
                                        onClick={() => setActive(g)}
                                        className={`w-full text-left rounded-lg px-3 py-2 text-sm border ${active?.id === g.id ? "bg-secondary border-border" : "border-transparent hover:bg-secondary/50"}`}
                                        data-testid="guests-item"
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="font-medium">{g.name}</div>
                                            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                                {g.tier}
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">{g.email}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="lg:col-span-8">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Guest profile
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!detail ? (
                            <LoadingBlock rows={5} />
                        ) : (
                            <div className="space-y-5">
                                <div className="flex flex-wrap items-baseline gap-3">
                                    <div className="text-xl font-semibold">{detail.guest.name}</div>
                                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                                        {detail.guest.tier} · {detail.guest.language.toUpperCase()}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="rounded-lg border border-border p-3">
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                            Preferences
                                        </div>
                                        {Object.keys(detail.guest.preferences || {}).length === 0 ? (
                                            <div className="text-sm text-muted-foreground">None recorded.</div>
                                        ) : (
                                            <ul className="text-sm space-y-1">
                                                {Object.entries(detail.guest.preferences).map(([k, v]) => (
                                                    <li key={k}>
                                                        <span className="text-muted-foreground">{k}:</span>{" "}
                                                        <span className="font-medium">{String(v)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-border p-3">
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                                            <Accessibility className="w-3.5 h-3.5" /> Accessibility
                                        </div>
                                        {Object.keys(detail.guest.accessibility || {}).length === 0 ? (
                                            <div className="text-sm text-muted-foreground">None recorded.</div>
                                        ) : (
                                            <ul className="text-sm space-y-1">
                                                {Object.entries(detail.guest.accessibility).map(([k, v]) => (
                                                    <li key={k}>
                                                        <span className="text-muted-foreground">{k}:</span>{" "}
                                                        <span className="font-medium">{String(v)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                                        Stays ({detail.stays.length})
                                    </div>
                                    <div className="space-y-1">
                                        {detail.stays.length === 0 ? (
                                            <div className="text-sm text-muted-foreground">No stays yet.</div>
                                        ) : (
                                            detail.stays.map((s) => (
                                                <div
                                                    key={s.id}
                                                    className="flex items-center justify-between text-sm bg-secondary/60 rounded-lg px-3 py-2"
                                                >
                                                    <div>
                                                        <span className="font-medium">Room {s.room_number}</span>{" "}
                                                        <span className="text-muted-foreground">
                                                            {s.check_in} → {s.check_out}
                                                        </span>
                                                    </div>
                                                    <StatusChip status={s.status} />
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
